import { write, getColumnsCount } from './write.js';
import { moveDownLines, moveUpLines, clearLine, stripAnsi } from './ansiHelpers.js';
import { Sticky } from './Sticky.js';
import fs from 'fs';
import path from 'path';

export class Writer {
  private _stickies: Sticky[] = [];
  private _lastStickies: string[] = [];
  private _totalLines: number = 0;
  private _logFileStream: fs.WriteStream | undefined;

  get stickies(): Sticky[] {
    return this._stickies;
  }

  set stickies(newStickies: (Sticky | string | undefined)[]) {
    for (const sticky of this._stickies) {
      sticky.removeAllListeners();
    }

    this._stickies = [];

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < newStickies.length; i++) {
      let sticky = newStickies[i];

      if (sticky !== undefined) {
        if (typeof sticky === 'string') {
          sticky = new Sticky(sticky);
        }

        sticky.on('update', () => this._reprintStickies());
        sticky.on('remove', () => this._removeSticky(sticky as Sticky));

        this._stickies.push(sticky);
      }
    }

    this._reprintStickies();
  }

  write(text: string): void {
    this._clearPreviousStickies();
    write(`${text}\n`);
    this._totalLines += this._countLines(text);
    this._printStickies();

    // Only process and write to log file when it's enabled, to avoid the stripAnsi overhead
    if (this._logFileStream) {
      try {
        this._logFileStream.write(`${stripAnsi(text)}\n`);
      } catch {
        // Do not console error here, as it will cause recursive calls to the writer.
        // This is a silent failure to avoid breaking the main functionality.
      }
    }
  }

  private _printStickies(): void {
    this._lastStickies = [];

    if (this._stickies.length > 0) {
      for (const sticky of this._stickies) {
        write(`${sticky.text}\n`);
        this._totalLines += this._countLines(sticky.text);
        this._lastStickies.push(sticky.text);
      }
    }
  }

  private _countLines(text: string): number {
    const columns = getColumnsCount();
    // total number of lines that this input took.
    let totalNumberOfLines = 0;
    const lines = text.split('\n');

    for (const item of lines) {
      // Remove the ANSI escape codes from the string and get the length
      const charCount = stripAnsi(item).length;

      // If the charCount is 0, it means that the string was empty. In that case, we still need to consider it as 1 line.
      // Otherwise, we need to calculate the number of lines that the string took by dividing the charCount by the number of columns.
      const lineCount = charCount === 0 ? 1 : Math.ceil(charCount / columns);

      totalNumberOfLines += lineCount;
    }

    return totalNumberOfLines;
  }

  private _clearPreviousStickies(): void {
    // Clear previous stickies first.
    if (this._lastStickies.length > 0 && this._totalLines > 0) {
      // total number of lines that the last stickies took.
      let totalNumberOfStickyLines = 0;
      for (const item of this._lastStickies) {
        totalNumberOfStickyLines += this._countLines(item);
      }

      this._clearLines(totalNumberOfStickyLines);

      this._lastStickies = [];
    }
  }

  private _clearLines(count: number): void {
    // Make sure that the count is not negative and does not exceed the total number of lines.
    count = Math.min(Math.max(0, count), this._totalLines);

    // Clear the screen and move the cursor to the top left corner.
    moveUpLines(count);

    for (let i = 0; i < count; i++) {
      clearLine();
      moveDownLines(1);
    }

    moveUpLines(count);
    this._totalLines -= count;
  }

  public clear(): void {
    this._clearLines(this._totalLines);
  }

  private _reprintStickies(): void {
    this._clearPreviousStickies();
    this._printStickies();
  }

  private _removeSticky(sticky: Sticky): void {
    this.stickies.splice(this.stickies.indexOf(sticky), 1);
    this._reprintStickies();
  }

  enableLogFile(cwd?: string): void {
    if (this._logFileStream) {
      try {
        this._logFileStream.end();
      } catch (err) {
        console.error(`Failed to close existing log file stream: ${err}`);
      }
    }

    const timestamp = new Date().toISOString().replace(/:/g, '-');
    // eslint-disable-next-line no-restricted-properties
    const dirname = cwd ?? process.cwd();
    const filePath = path.join(dirname, `cloudpack-${timestamp}.log`);

    try {
      fs.mkdirSync(dirname, { recursive: true });
      this._logFileStream = fs.createWriteStream(filePath, { flags: 'a' });
    } catch (err) {
      console.error(`Failed to open log file: ${err}`);
    }
  }

  public dispose(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this._logFileStream) {
        // Write any remaining data and then close the stream
        this._logFileStream.end(() => {
          this._logFileStream = undefined;
          resolve();
        });
      } else {
        resolve();
      }
    });
  }
}
